import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #isMegaMenuHydrated = false;
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    // Hydrate deferred mega-menu templates after page DOM is ready or loaded
    const hydrateAll = () => {
      document.querySelectorAll('.menu-list__submenu').forEach(hydrateSubmenu);
      this.#isMegaMenuHydrated = true;
    };
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      setTimeout(hydrateAll, 50);
    }
    window.addEventListener('load', () => setTimeout(hydrateAll, 50), { once: true });
    document.addEventListener('DOMContentLoaded', () => setTimeout(hydrateAll, 50), { once: true });

    // Defer mega-menu image preload to first hover — avoids stripping loading="lazy"
    // from every menu image on page load before the user has touched the nav.
    this.addEventListener('pointerenter', this.#preloadImages, { once: true });
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerenter', this.#preloadImages);
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    if (this.#deactivateTimer) {
      clearTimeout(this.#deactivateTimer);
      this.#deactivateTimer = undefined;
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #deactivateTimer;

  /**
   * Animation frame ID for pointer target reconciliation.
   * @type {number | null}
   */
  #reconcileFrameId = null;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || Math.abs(event.movementY) >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    }

    this.#reconcilePointerTarget();
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    if (this.#reconcileFrameId) return;
    this.#reconcileFrameId = requestAnimationFrame(() => {
      this.#reconcileFrameId = null;
      const { x, y } = this.#lastPointer;
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: true, composed: true }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
        item.closest('.menu-list__list-item')?.style.setProperty('--header-height',`${document.querySelector('.header')?.offsetHeight}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    if (this.#reconcileFrameId) {
      cancelAnimationFrame(this.#reconcileFrameId);
      this.#reconcileFrameId = null;
    }
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (this.#deactivateTimer) {
      clearTimeout(this.#deactivateTimer);
      this.#deactivateTimer = undefined;
    }

    if (!this.#isMegaMenuHydrated) {
      document.querySelectorAll('.menu-list__submenu').forEach(hydrateSubmenu);
      this.#isMegaMenuHydrated = true;
    }

    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    if (submenu) {
      hydrateSubmenu(submenu);
    }
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Force WebKit layout reflow for Safari
      void submenu.offsetHeight;

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration).
      // This is especially important for the 3rd+ menu items where hydrated content may arrive
      // after the initial height measurement reads 0.
      this.#submenuMutationObserver = new MutationObserver(() => {
        // Only update height if this submenu is still the active one
        if (!this.#state.activeItem) return;
        const activeSubmenu = findSubmenu(this.#state.activeItem);
        if (activeSubmenu !== submenu) return;

        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated
          // and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            const newHeight = submenu.offsetHeight;
            if (newHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${newHeight}px`);
              this.#setFullOpenHeaderHeight(newHeight);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 3000ms to prevent memory leaks.
      // Increased from 500ms to survive slow section hydration fetches (e.g. 3rd+ menu items
      // whose content is deferred until the idle-triggered SRA hydration completes).
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 3000);
    }

    // Remove data-active from all other submenus outside of the currently active submenu
    document.querySelectorAll('.menu-list__submenu[data-active]').forEach((s) => {
      if (s !== submenu && (!submenu || !submenu.contains(s))) {
        delete s.dataset.active;
      }
    });

    let finalHeight = submenu?.offsetHeight || 0;

    if (finalHeight === 0 && submenu) {
      const inner = submenu.querySelector('.menu-list__submenu-inner') || submenu.firstElementChild;
      finalHeight = inner?.offsetHeight || inner?.scrollHeight || submenu.scrollHeight || 0;
    }

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);

    // Safari layout update on next frame
    requestAnimationFrame(() => {
      if (this.#state.activeItem !== item) return;
      const realHeight =
        submenu?.offsetHeight ||
        submenu?.querySelector('.menu-list__submenu-inner')?.offsetHeight ||
        submenu?.scrollHeight ||
        0;
      if (realHeight > 0) {
        this.headerComponent?.style.setProperty('--submenu-height', `${realHeight}px`);
        this.#setFullOpenHeaderHeight(realHeight);
      }
    });
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const relatedTarget = event.relatedTarget instanceof Node ? event.relatedTarget : null;

    const isMovingWithinMenu =
      relatedTarget &&
      (menu?.contains(relatedTarget) ||
        menu?.contains(document.activeElement) ||
        this.#state.activeItem?.parentElement?.contains(relatedTarget));

    const isMovingToSubmenu =
      relatedTarget &&
      (event.type === 'blur' || event.type === 'pointerleave') &&
      menu?.contains(relatedTarget);

    const isMovingToOverflowMenu =
      relatedTarget && relatedTarget.parentElement?.matches('[slot="overflow"]');

    const isMenuHovered = menu?.matches(':hover') ?? false;
    const isListItemHovered = this.#state.activeItem?.parentElement?.matches(':hover') ?? false;

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu || isMenuHovered || isListItemHovered) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    if (this.#deactivateTimer) {
      clearTimeout(this.#deactivateTimer);
    }

    this.#deactivateTimer = setTimeout(() => {
      this.#deactivateTimer = undefined;
      this.#deactivate();
    }, 150);
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (this.#deactivateTimer) {
      clearTimeout(this.#deactivateTimer);
      this.#deactivateTimer = undefined;
    }

    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu, overflow list, or active submenu is still being hovered
    const menu = findSubmenu(item);
    if (
      this.overflowListHovered ||
      this.overflowMenu?.matches(':hover') ||
      menu?.matches(':hover') ||
      item.parentElement?.matches(':hover') ||
      item.matches(':hover')
    ) {
      return;
    }

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from all submenus that are not currently hovered
    document.querySelectorAll('.menu-list__submenu[data-active]').forEach((s) => {
      if (!s.matches(':hover')) {
        delete s.dataset.active;
      }
    });
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}


if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  if (element.matches('[ref="menuitem"]')) return /** @type {HTMLElement} */ (element);

  const matchedItem = element.closest('[ref="menuitem"]') || element.closest('.menu-list__list-item')?.querySelector('[ref="menuitem"]');
  if (matchedItem instanceof HTMLElement) return matchedItem;

  return /** @type {HTMLElement | null} */ (element.querySelector('[ref="menuitem"]'));
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  if (!(element instanceof Element)) return null;
  const submenu = element.closest('.menu-list__list-item')?.querySelector('[ref="submenu[]"]') || element.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
  * Hydrate deferred mega menu template into DOM.
  * @param {HTMLElement | null} submenu
  */
function hydrateSubmenu(submenu) {
  if (!submenu || !(submenu instanceof HTMLElement)) return;
  const template = submenu.querySelector('template.js-mega-menu-template');
  if (template) {
    submenu.appendChild(template.content.cloneNode(true));
    template.remove();
  }
}
